import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, Selector } from 'sonata-react-components';

import { groupingMessages } from '../groupingMessages';
import { optionValues } from '../selectorOptions';

import type { GroupFamily } from '../../domain/grouping.types';
import type { GroupingItem } from '../../hooks/useGroupingItems';

export interface AddMembersRowProps {
  /** Family items that are not members of the edited group. */
  candidates: GroupingItem[];
  family: GroupFamily;
  onAdd: (ids: string[]) => void;
}

/** "Add POIs/Screens/Areas" link that reveals a selector of candidates plus an "Add" button (every picked item is added). */
export function AddMembersRow({ candidates, family, onAdd }: Readonly<AddMembersRowProps>) {
  const { formatMessage } = useIntl();
  const [isAdding, setIsAdding] = useState(false);
  const [pendingIds, setPendingIds] = useState<string[]>([]);
  const options = candidates.map(item => ({ id: item.id, label: item.name || item.detail, value: item.id }));

  if (!isAdding) {
    return (
      <div className="editGroupsModal__addLink">
        <ButtonUI
          icon="fa-solid fa-plus"
          id="editGroupsModal-startAdding"
          onClick={() => setIsAdding(true)}
          shape="link"
          text={formatMessage(groupingMessages.addItems, { family })}
          type="tertiary"
        />
      </div>
    );
  }

  const handleAdd = () => {
    onAdd(pendingIds);
    setPendingIds([]);
  };

  return (
    <div className="editGroupsModal__add">
      <span className="editGroupsModal__addHint">{formatMessage(groupingMessages.addItemsHint)}</span>
      <div className="editGroupsModal__addControls">
        <div className="editGroupsModal__addSelector">
          <Selector
            isDisabled={options.length === 0}
            onChange={selected => setPendingIds(optionValues(selected))}
            options={options}
            placeholder={formatMessage(
              options.length ? groupingMessages.addItemsPlaceholder : groupingMessages.noItemsToAdd,
              { family }
            )}
            tiny
            type="multiple"
            value={options.filter(option => pendingIds.includes(option.value))}
          />
        </div>
        <ButtonUI
          disabled={pendingIds.length === 0}
          icon="fa-solid fa-plus"
          id="editGroupsModal-addMembers"
          onClick={handleAdd}
          shape="squareRounded"
          size="ultraSmall"
          text={formatMessage(groupingMessages.add)}
          type="secondary"
        />
      </div>
    </div>
  );
}
